import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Clock,
  Printer,
  FileDown,
  RefreshCw,
  CheckCircle2,
  AlertCircle,
  XCircle,
  PlayCircle,
  Calendar,
  Layers,
  IndianRupee,
  Search,
  ExternalLink,
  Play,
  Zap,
  ZapOff,
  AlertTriangle,
  RotateCcw,
  Check,
  FileText,
  Image,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { autoPrintService } from "@/lib/autoPrintService";

const STATUS_CONFIG = {
  pending: {
    label: "Pending",
    bg: "bg-amber-500/10",
    text: "text-amber-400",
    border: "border-amber-500/30",
    desc: "Waiting in queue for printing",
  },
  processing: {
    label: "Processing",
    bg: "bg-blue-500/10",
    text: "text-blue-400",
    border: "border-blue-500/30",
    desc: "Document being prepared",
  },
  printing: {
    label: "Printing...",
    bg: "bg-amber-500/20",
    text: "text-amber-300",
    border: "border-amber-400/50",
    desc: "Sending to Windows print spooler",
    pulse: true,
  },
  printer_offline: {
    label: "Printer Offline",
    bg: "bg-orange-500/15",
    text: "text-orange-400",
    border: "border-orange-500/40",
    desc: "Assigned printer is offline — will auto-print when online",
  },
  printed: {
    label: "Printed ✓",
    bg: "bg-emerald-500/10",
    text: "text-emerald-400",
    border: "border-emerald-500/30",
    desc: "Print completed",
  },
  cancelled: {
    label: "Cancelled",
    bg: "bg-zinc-800",
    text: "text-zinc-400",
    border: "border-zinc-700",
    desc: "Cancelled by operator",
  },
  failed: {
    label: "Failed",
    bg: "bg-red-500/10",
    text: "text-red-400",
    border: "border-red-500/30",
    desc: "Print spooler error",
  },
};

export default function PrintJobsQueue() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [shop, setShop] = useState(null);
  const [shopId, setShopId] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [updatingId, setUpdatingId] = useState("");
  const [printingId, setPrintingId] = useState("");
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [autoPrintEnabled, setAutoPrintEnabled] = useState(false);

  const isElectron = Boolean(window.jsmDesktop?.printers?.getAll);

  // -------------------------------------------------------------
  // Load Jobs & Shop Configuration
  // -------------------------------------------------------------
  const loadJobs = useCallback(async (showToast = false) => {
    setError("");
    try {
      let currentShopId = shopId;
      if (!currentShopId) {
        const shopRes = await api.get("/scan-to-print/shop");
        if (shopRes.data?.shop) {
          const loadedShop = shopRes.data.shop;
          setShop(loadedShop);
          currentShopId = loadedShop.shop_id;
          setShopId(currentShopId);
          setAutoPrintEnabled(Boolean(loadedShop.auto_print_enabled));
        }
      }

      if (!currentShopId) {
        setLoading(false);
        return;
      }

      const res = await api.get(`/scan-to-print/shop/${currentShopId}/jobs`);
      if (res.data?.jobs) {
        setJobs(res.data.jobs);
        if (showToast) toast.success("Job queue refreshed");
      }
    } catch (err) {
      console.warn("Could not load jobs:", err);
      setError(formatApiError(err) || "Unable to load print jobs");
      if (showToast) toast.error("Failed to refresh jobs: " + formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [shopId]);

  useEffect(() => {
    loadJobs();
  }, [loadJobs]);

  // Periodic polling
  useEffect(() => {
    if (!autoRefresh) return;
    const timer = setInterval(() => {
      loadJobs(false);
    }, 4000);
    return () => clearInterval(timer);
  }, [autoRefresh, loadJobs]);

  // Subscribe to auto-print notifications
  useEffect(() => {
    const unsubscribe = autoPrintService.subscribe((event) => {
      if (event.type === "printed_success") {
        toast.success(event.message || "Job auto-printed successfully!");
        loadJobs(false);
      } else if (event.type === "printer_offline") {
        toast.warning(event.message);
        loadJobs(false);
      } else if (event.type === "printing_started") {
        loadJobs(false);
      } else if (event.type === "print_failed") {
        toast.error(event.message);
        loadJobs(false);
      }
    });

    return () => unsubscribe();
  }, [loadJobs]);

  const handleToggleAutoPrint = (enabled) => {
    setAutoPrintEnabled(enabled);
    autoPrintService.setAutoPrintEnabled(enabled);
    if (enabled) {
      toast.success("⚡ Auto-Print enabled — incoming jobs will print directly to Windows spooler.");
    } else {
      toast.info("Auto-Print paused — manual printing enabled.");
    }
  };

  // -------------------------------------------------------------
  // Update Job Status
  // -------------------------------------------------------------
  const handleUpdateStatus = async (jobId, newStatus) => {
    setUpdatingId(jobId);
    try {
      const formData = new FormData();
      formData.append("status", newStatus);

      await api.put(`/scan-to-print/jobs/${jobId}/status`, formData);
      setJobs((prev) =>
        prev.map((j) => (j.job_id === jobId ? { ...j, status: newStatus } : j))
      );
      toast.success(`Job status updated: ${newStatus.toUpperCase()}`);
    } catch (err) {
      toast.error("Failed to update status: " + formatApiError(err));
    } finally {
      setUpdatingId("");
    }
  };

  // -------------------------------------------------------------
  // Manual Direct Print
  // -------------------------------------------------------------
  const handleManualPrint = async (job) => {
    setPrintingId(job.job_id);
    try {
      toast.info(`Sending Job #${job.job_id} to printer...`);
      const result = await autoPrintService.printJobManually(job, shop);
      if (result?.success) {
        toast.success(`Job #${job.job_id} sent to printer! ✓`);
        await handleUpdateStatus(job.job_id, "printed");
      }
    } catch (err) {
      console.error("Manual print error:", err);
      toast.error("Failed to print: " + (err.message || "Spooler error"));
    } finally {
      setPrintingId("");
    }
  };

  const handleDownload = async (jobId, filename) => {
    try {
      toast.info("Downloading original document...");
      const res = await api.get(`/scan-to-print/jobs/${jobId}/download`, {
        responseType: "blob",
      });

      const isPdf = filename?.toLowerCase().endsWith(".pdf");
      const mimeType = res.headers?.["content-type"] || (isPdf ? "application/pdf" : "application/octet-stream");
      const blob = new Blob([res.data], { type: mimeType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const downloadName = filename || `job-${jobId}.pdf`;
      link.setAttribute("download", downloadName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 3000);
      toast.success(`Downloaded: ${downloadName}`);
    } catch (err) {
      console.error("Download error:", err);
      toast.error("Download failed: " + formatApiError(err));
    }
  };

  const filteredJobs = useMemo(() => {
    return jobs.filter((j) => {
      const matchesSearch =
        (j.job_id || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
        (j.original_filename || "").toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStatus = statusFilter === "all" || j.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [jobs, searchQuery, statusFilter]);

  const offlineJobsCount = useMemo(
    () => jobs.filter((j) => j.status === "printer_offline").length,
    [jobs]
  );

  return (
    <div className="min-h-[calc(100vh-4rem)] p-4 md:p-6 max-w-6xl mx-auto flex flex-col gap-6 select-none animate-fade-in">
      {/* Header */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 bg-zinc-900/70 border border-zinc-800 rounded-2xl p-5 backdrop-blur-md shadow-xl">
        <div>
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-center justify-center text-amber-400">
              <Clock className="w-4 h-4" />
            </div>
            <h1 className="text-xl md:text-2xl font-bold tracking-tight text-white flex items-center gap-1.5">
              <span>Print</span>
              <span className="text-amber-400">Q</span>
              <span className="text-zinc-400 font-medium text-lg ml-1">Live Queue</span>
            </h1>
          </div>
          <p className="text-xs md:text-sm text-zinc-400 mt-1">
            Incoming customer mobile scans, PDF uploads, and automated Windows printing spooler.
          </p>
        </div>

        {/* Auto-Print Toggle & Refresh */}
        <div className="flex items-center gap-3 flex-wrap">
          {isElectron && (
            <button
              type="button"
              onClick={() => handleToggleAutoPrint(!autoPrintEnabled)}
              className={`flex items-center gap-2 px-3.5 py-2 rounded-xl text-xs font-bold border transition-all active:scale-95 ${
                autoPrintEnabled
                  ? "bg-amber-500 text-zinc-950 border-amber-400 shadow-md shadow-amber-500/20"
                  : "bg-zinc-800 text-zinc-400 border-zinc-700 hover:text-white"
              }`}
            >
              {autoPrintEnabled ? <Zap className="w-3.5 h-3.5 fill-current" /> : <ZapOff className="w-3.5 h-3.5" />}
              <span>{autoPrintEnabled ? "⚡ Auto-Print: ON" : "Auto-Print: OFF"}</span>
            </button>
          )}

          <button
            onClick={() => loadJobs(true)}
            className="flex items-center gap-2 px-3.5 py-2 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-white text-xs font-semibold border border-zinc-700 transition-all active:scale-95 cursor-pointer"
          >
            <RefreshCw className="w-3.5 h-3.5" />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Offline Alert Banner (if any jobs waiting) */}
      {offlineJobsCount > 0 && (
        <div className="bg-orange-500/10 border border-orange-500/40 rounded-2xl p-4 flex items-center justify-between text-xs text-orange-300 shadow-lg animate-fade-in">
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-orange-400 shrink-0" />
            <div>
              <strong className="text-white block font-bold">
                {offlineJobsCount} Job(s) Waiting for Printer to Come Online
              </strong>
              <span className="text-zinc-400">
                PrintQ will automatically retry and print queued jobs the moment your assigned Windows printer connects.
              </span>
            </div>
          </div>
          <button
            type="button"
            onClick={() => autoPrintService.tick()}
            className="px-3 py-1.5 rounded-xl bg-orange-500 text-zinc-950 font-bold text-xs hover:bg-orange-400 transition-colors shrink-0"
          >
            Retry Now
          </button>
        </div>
      )}

      {/* Filter & Search Bar */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 bg-zinc-900/80 border border-zinc-800 rounded-2xl p-4 shadow-lg">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input
            type="text"
            placeholder="Search by Job ID or file name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full bg-zinc-950 border border-zinc-800 rounded-xl pl-10 pr-4 py-2 text-xs text-white placeholder-zinc-500 focus:outline-none focus:border-amber-500"
          />
        </div>

        <div className="flex items-center gap-1.5 overflow-x-auto pb-1 sm:pb-0 scrollbar-none">
          {[
            { id: "all", label: "All" },
            { id: "pending", label: "Pending" },
            { id: "printing", label: "Printing" },
            { id: "printer_offline", label: "Offline Queue" },
            { id: "printed", label: "Printed" },
            { id: "cancelled", label: "Cancelled" },
          ].map((s) => (
            <button
              key={s.id}
              onClick={() => setStatusFilter(s.id)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-colors cursor-pointer ${
                statusFilter === s.id
                  ? "bg-amber-500 text-zinc-950 font-bold"
                  : "bg-zinc-950 text-zinc-400 border border-zinc-800 hover:text-white"
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {/* Job List Cards */}
      <div className="flex flex-col gap-3">
        {loading ? (
          <div className="py-16 text-center text-xs text-zinc-500 flex items-center justify-center gap-2">
            <RefreshCw className="w-4 h-4 animate-spin text-amber-400" />
            <span>Loading job queue...</span>
          </div>
        ) : error && jobs.length === 0 ? (
          <div className="bg-rose-500/10 border border-rose-500/30 rounded-2xl p-8 text-center flex flex-col items-center gap-3 text-rose-300">
            <AlertCircle className="w-8 h-8 text-rose-400" />
            <p className="text-sm font-bold text-white">Unable to load print jobs</p>
            <p className="text-xs text-zinc-400 max-w-sm">{error}</p>
            <button
              onClick={() => loadJobs(true)}
              className="px-4 py-2 rounded-xl bg-amber-500 text-zinc-950 font-bold text-xs hover:bg-amber-400 transition-colors cursor-pointer"
            >
              Retry
            </button>
          </div>
        ) : filteredJobs.length > 0 ? (
          filteredJobs.map((job) => {
            const sc = STATUS_CONFIG[job.status] || STATUS_CONFIG.pending;
            const isUpdating = updatingId === job.job_id;
            const isPrinting = printingId === job.job_id || job.status === "printing";
            const isPdf =
              job.original_filename?.toLowerCase().endsWith(".pdf") ||
              job.content_type?.includes("pdf");

            return (
              <div
                key={job.job_id}
                className={`bg-zinc-900/80 border rounded-2xl p-5 flex flex-col md:flex-row md:items-center justify-between gap-4 transition-all duration-150 shadow-md ${
                  job.status === "printer_offline"
                    ? "border-orange-500/40 bg-orange-950/10"
                    : job.status === "printing"
                    ? "border-amber-500/50 bg-amber-950/10"
                    : "border-zinc-800 hover:border-zinc-700/80"
                }`}
              >
                {/* Left Job Info */}
                <div className="flex items-start gap-4">
                  <div
                    className={`w-10 h-10 rounded-xl border flex items-center justify-center flex-shrink-0 ${
                      isPdf
                        ? "bg-rose-500/10 border-rose-500/30 text-rose-400"
                        : "bg-zinc-950 border-zinc-800 text-amber-400"
                    }`}
                  >
                    {isPdf ? <FileText className="w-5 h-5" /> : <Printer className="w-5 h-5" />}
                  </div>

                  <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-2.5 flex-wrap">
                      <span className="font-mono text-sm font-bold text-white">
                        {job.job_id}
                      </span>
                      <span
                        className={`text-[10px] font-bold px-2.5 py-0.5 rounded-full border ${sc.bg} ${sc.text} ${sc.border} ${
                          sc.pulse ? "animate-pulse" : ""
                        }`}
                      >
                        {sc.label}
                      </span>
                      <span
                        className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase border ${
                          isPdf
                            ? "bg-rose-500/15 text-rose-300 border-rose-500/40"
                            : "bg-blue-500/15 text-blue-300 border-blue-500/40"
                        }`}
                      >
                        {isPdf ? "PDF Document" : "Image"}
                      </span>
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-zinc-800 text-zinc-300 font-bold uppercase">
                        {job.print_type || "BW"}
                      </span>
                    </div>

                    <p className="text-xs text-zinc-200 font-bold truncate max-w-md" title={job.original_filename}>
                      {job.original_filename || "Document"}
                    </p>

                    <div className="flex items-center gap-4 text-[11px] text-zinc-400 mt-1 flex-wrap">
                      <span>Source: <b className="text-zinc-200">{isPdf ? "Original PDF" : "High-Res Image"}</b></span>
                      <span>Pages: <b className="text-zinc-200">{job.pages || 1}</b></span>
                      <span>Copies: <b className="text-zinc-200">{job.copies || 1}</b></span>
                      <span>Total Sheets: <b className="text-zinc-200">{job.total_pages || (job.pages * (job.copies || 1))}</b></span>
                      {job.total_amount && (
                        <span className="text-emerald-400 font-bold font-mono">
                          ₹{job.total_amount}
                        </span>
                      )}
                      <span>Time: {new Date(job.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
                    </div>
                  </div>
                </div>

                {/* Right Action Buttons */}
                <div className="flex items-center gap-2 flex-wrap">
                  {/* Print Now / Manual Print */}
                  <button
                    type="button"
                    disabled={isPrinting}
                    onClick={() => handleManualPrint(job)}
                    className="px-4 py-2 rounded-xl bg-gradient-to-r from-amber-400 via-amber-500 to-amber-600 hover:brightness-110 text-zinc-950 text-xs font-black transition-all shadow-md shadow-amber-500/20 flex items-center gap-1.5 active:scale-95 cursor-pointer disabled:opacity-50"
                  >
                    <Play className="w-3.5 h-3.5 fill-current" />
                    <span>{isPrinting ? "Printing..." : "Print Now"}</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => handleDownload(job.job_id, job.original_filename)}
                    className="p-2.5 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-xs font-semibold flex items-center gap-1.5 transition-colors active:scale-95 cursor-pointer"
                    title={`Download original ${isPdf ? "PDF" : "image"}`}
                  >
                    <FileDown className="w-4 h-4" />
                  </button>

                  {job.status === "printer_offline" && (
                    <button
                      type="button"
                      onClick={() => handleManualPrint(job)}
                      disabled={isPrinting}
                      className="px-3.5 py-2 rounded-xl bg-orange-500/20 hover:bg-orange-500/30 text-orange-400 text-xs font-bold border border-orange-500/40 transition-all active:scale-95 cursor-pointer flex items-center gap-1"
                    >
                      <RotateCcw className="w-3.5 h-3.5" />
                      <span>Retry</span>
                    </button>
                  )}

                  {job.status !== "printed" && (
                    <button
                      type="button"
                      onClick={() => handleUpdateStatus(job.job_id, "printed")}
                      disabled={isUpdating}
                      className="px-3 py-2 rounded-xl bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400 border border-emerald-500/40 text-xs font-bold transition-all flex items-center gap-1 active:scale-95 cursor-pointer"
                      title="Mark as Printed"
                    >
                      <Check className="w-3.5 h-3.5" />
                      <span>Done</span>
                    </button>
                  )}

                  {job.status !== "cancelled" && job.status !== "printed" && (
                    <button
                      type="button"
                      onClick={() => handleUpdateStatus(job.job_id, "cancelled")}
                      disabled={isUpdating}
                      className="p-2 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-zinc-400 hover:text-red-400 text-xs transition-colors cursor-pointer"
                      title="Cancel Job"
                    >
                      <XCircle className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            );
          })
        ) : (
          <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-2xl p-12 text-center flex flex-col items-center gap-2 text-zinc-500">
            <Printer className="w-8 h-8 stroke-[1.2]" />
            <p className="text-xs font-medium">No print jobs found in queue</p>
          </div>
        )}
      </div>
    </div>
  );
}
